export interface EmoticonConversion {
    start: number
    emoticon: string
    emoji: string
}

export interface TextWithCursor {
    text: string
    cursor: number
}

// Longer variants first, so e.g. `</3` wins over `<3`
const emoticonToEmoji: [string, string][] = [
    ['</3', '💔'],
    ['<3', '❤️'],
    [':-)', '🙂'],
    [':)', '🙂'],
    [';-)', '😉'],
    [';)', '😉'],
    [':\'(', '😢'],
    [':-(', '🙁'],
    [':(', '🙁'],
    [':-D', '😄'],
    [':D', '😄'],
    [':-P', '😛'],
    [':-p', '😛'],
    [':P', '😛'],
    [':p', '😛'],
    [':-O', '😮'],
    [':-o', '😮'],
    [':O', '😮'],
    [':o', '😮'],
    [':-*', '😘'],
    [':*', '😘'],
    [':-|', '😐'],
    [':|', '😐'],
    [':-/', '😕'],
    [':/', '😕'],
]

// Start of the text, whitespace or an emoji (so emoticons can be chained, e.g. `<3<3`). `️` is the
// variation selector that ends emoji like ❤️.
const emoticonBoundaryPattern = /(?:^|[\s\p{Extended_Pictographic}️])$/u

/**
 * Replaces an emoticon that ends right before the cursor with its emoji. Only matches emoticons at the
 * start of the text or after whitespace or an emoji, so URLs (`https://`) and text like `(see 2:)` are left alone.
 */
export const convertEmoticonBeforeCursor = (
    text: string,
    cursor: number,
): (TextWithCursor & { conversion: EmoticonConversion }) | undefined => {
    const beforeCursor = text.slice(0, cursor)
    const match = emoticonToEmoji.find(([emoticon]) => {
        if (! beforeCursor.endsWith(emoticon)) {
            return false
        }

        return emoticonBoundaryPattern.test(beforeCursor.slice(0, -emoticon.length))
    })

    if (! match) {
        return undefined
    }

    const [emoticon, emoji] = match
    const start = cursor - emoticon.length

    return {
        text: text.slice(0, start) + emoji + text.slice(cursor),
        cursor: start + emoji.length,
        conversion: { start, emoticon, emoji },
    }
}

/**
 * Restores the original emoticon of a conversion, provided its emoji is still in place in the given text
 */
export const revertEmoticonConversion = (
    text: string,
    { start, emoticon, emoji }: EmoticonConversion,
): TextWithCursor | undefined => {
    if (text.slice(start, start + emoji.length) !== emoji) {
        return undefined
    }

    return {
        text: text.slice(0, start) + emoticon + text.slice(start + emoji.length),
        cursor: start + emoticon.length,
    }
}
